<template lang="pug">
//- Nothing to choose when the environment offers a single region: the create
//- request falls back to that one anyway, so the field would be dead weight.
.bq-region-field(v-if="regions.length > 1")
    label.bq-field-label(:for="fieldId") Region
    select.bq-region-select(
        :id="fieldId"
        :value="modelValue"
        :disabled="disabled"
        @change="pick($event.target.value)"
    )
        option(v-for="(r, i) of regions" :key="r.region" :value="r.region") {{ optionLabel(r, i) }}
    p.bq-region-hint This project's data is stored in the region you pick, and cannot be moved later.
</template>

<script setup lang="ts">
import { computed, onMounted, watch, ref } from "vue";
import { skapi } from "@/main";
import {
    closestRegion,
    isAvailableRegion,
    isLocatedLocale,
    regionsForLocale,
    type ServiceRegion,
} from "@/code/regions";

const props = withDefaults(
    defineProps<{
        modelValue: string;
        disabled?: boolean;
        fieldId?: string;
    }>(),
    { disabled: false, fieldId: "projectRegion" }
);
const emit = defineEmits(["update:modelValue"]);

// The visitor's country as the SDK saw it. skapi.connection is filled in during
// init so it is normally already there; when it is not, the list starts in
// VITE_REG order and re-sorts (and re-defaults) the moment it arrives.
let locale = ref(skapi.connection?.locale || "");

const regions = computed(() => regionsForLocale(locale.value));

const optionLabel = (r: ServiceRegion, i: number): string =>
    i === 0 && isLocatedLocale(locale.value) ? `${r.label} (closest to you)` : r.label;

const pick = (region: string) => emit("update:modelValue", region);

// Owning the default here keeps the two create forms from each carrying their
// own copy of it, and re-applies it when a form resets to an empty value.
const applyDefault = () => {
    if (!isAvailableRegion(props.modelValue)) {
        pick(closestRegion(locale.value));
    }
};

onMounted(async () => {
    applyDefault();

    if (locale.value) return;

    try {
        let info: any = await skapi.getConnectionInfo();
        if (info?.locale) {
            locale.value = info.locale;
            // The ranking just changed under the untouched default.
            applyDefault();
        }
    } catch (err) {
        // No locale: the list stays in VITE_REG order and the first entry wins.
        console.error("Could not read connection locale:", err);
    }
});

watch(
    () => props.modelValue,
    (v) => {
        if (!v) applyDefault();
    }
);
</script>

<style scoped lang="less">
@import "@/assets/less/bq-tokens.less";

.bq-field-label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: @ink;
    margin-bottom: 0.4rem;
    letter-spacing: 0.02em;
}

.bq-region-select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid @input-line;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.9rem;
    outline: none;
    border-radius: 0;
    cursor: pointer;
    transition: border-color 0.15s;

    &:focus {
        border-color: @ink;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.bq-region-hint {
    margin: 0.4rem 0 0;
    font-size: 0.75rem;
    color: @muted;
    line-height: 1.5;
}
</style>
